import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, DollarSign, Clock, CheckCircle2, Users, Building2,
  TrendingUp, Loader2, Gift, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const TalentBonusTracker = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchBonusData();
  }, []);

  const fetchBonusData = async () => {
    try {
      const res = await api.get('/referrals/bonus-tracker');
      setData(res.data);
    } catch {
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const { summary, breakdown, transactions, payouts } = data || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-bonus-tracker">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/home')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">My Earnings & Bonuses</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/talent/referrals')}>
            <Gift className="w-4 h-4 mr-1.5" />Referral Program
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">${(summary?.total_earned || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Total Earned</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-600">${(summary?.total_pending || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-600">${(summary?.total_paid || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Paid Out</p>
            </CardContent>
          </Card>
          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-cyan-700">${(summary?.ytd_earned || 0).toFixed(2)}</p>
              <p className="text-xs text-cyan-600 font-medium">YTD Earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Breakdown */}
        <Card data-testid="referral-breakdown">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm font-semibold">Referral Earnings Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-cyan-200 bg-cyan-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-medium text-gray-800">Talent Referrals ($25 each)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Earned:</span>
                  <span className="font-semibold text-green-600">${(breakdown?.talent_referrals?.earned || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Pending:</span>
                  <span className="font-medium text-amber-600">${(breakdown?.talent_referrals?.pending || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="p-4 rounded-lg border border-pink-200 bg-pink-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-pink-600" />
                  <span className="text-sm font-medium text-gray-800">Brand Referrals ($100 each)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Earned:</span>
                  <span className="font-semibold text-green-600">${(breakdown?.client_referrals?.earned || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Pending:</span>
                  <span className="font-medium text-amber-600">${(breakdown?.client_referrals?.pending || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card data-testid="transaction-history">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm font-semibold">Transaction History</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {(!transactions || transactions.length === 0) ? (
              <div className="text-center py-8">
                <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No transactions yet</p>
                <p className="text-xs text-gray-400 mt-1">Start referring to earn bonuses!</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/talent/referrals')}>
                  <Gift className="w-4 h-4 mr-1.5" />Go to Referrals
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid="transaction-item">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.referred_type === 'talent' ? 'bg-cyan-100' : 'bg-pink-100'}`}>
                        {tx.referred_type === 'talent' ? <Users className="w-4 h-4 text-cyan-600" /> : <Building2 className="w-4 h-4 text-pink-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{tx.referred_name}</p>
                        <p className="text-xs text-gray-500">
                          {tx.referred_type === 'talent' ? 'Talent referral' : 'Brand referral'} · {tx.date ? formatDateMonthDay(tx.date) : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${tx.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>${tx.amount.toFixed(2)}</p>
                      <Badge className={`text-[10px] ${tx.status === 'paid' ? 'bg-green-100 text-green-700' : tx.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        {tx.status === 'paid' ? 'Paid' : tx.status === 'pending' ? 'Pending' : 'Cancelled'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TalentBonusTracker;
